export type User = {
  id: number;
  username: string;
  displayName: string;
  email: string | null;
  role: 'user' | 'operator' | 'admin';
  // La API serializa los BigInt como string: un archivo puede pasar los 2 GB
  // y un number de JS no es el tipo correcto para representar bytes exactos.
  quotaBytes: string;
  theme: string;
};

export type FileEntry = {
  name: string;
  path: string;
  kind: 'file' | 'folder';
  sizeBytes: string;
  modifiedAt: string | null;
};

export type Listing = {
  path: string;
  entries: FileEntry[];
  quota: { usedBytes: string; totalBytes: string };
  /** Hay más en la carpeta: se piden al bajar. */
  siguiente: string | null;
};

export type SharedEntry = {
  id: number;
  name: string;
  objectKey: string;
  kind: 'file' | 'folder';
  sizeBytes: string;
  permission: 'read' | 'write';
  counterpart: string;
  createdAt: string;
};

export type PublicLinkEntry = {
  id: number;
  name: string;
  path: string;
  url: string;
  sizeBytes: string;
  expiresAt: string;
  downloadLimit: number | null;
  downloadCount: number;
  active: boolean;
};

export type Role = 'user' | 'operator' | 'admin';

export type AdminUser = {
  id: number;
  username: string;
  displayName: string;
  email: string | null;
  role: Role;
  quotaBytes: string;
  usedBytes: string;
  active: boolean;
  createdAt: string;
};

export type AdminSummary = {
  usuarios: { total: number; activos: number; porRol: Record<string, number> };
  almacenamiento: { usadoBytes: string; asignadoBytes: string };
  compartidos: number;
  linksActivos: number;
  actividad: Array<{
    action: string;
    actor: string | null;
    target: string | null;
    createdAt: string;
  }>;
};

export type ItemPapelera = {
  id: number;
  nombre: string;
  ubicacion: string;
  esCarpeta: boolean;
  sizeBytes: string;
  objetos: number;
  eliminadoEn: string;
  venceEn: string;
};

export type Destinatario = { id: number; displayName: string; detalle?: string };

export type FiltroAuditoria = {
  actor?: string;
  accion?: string;
  desde?: string;
  hasta?: string;
  texto?: string;
};

export type EventoAuditoria = {
  id: string;
  fecha: string;
  actor: string | null;
  accion: string;
  objetivo: string | null;
  detalle: string | null;
  ip: string | null;
};

function queryAuditoria(f: FiltroAuditoria, extra: Record<string, string> = {}): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries({ ...f, ...extra })) if (v) q.set(k, v);
  return q.toString();
}

export type OfficeConfig = {
  documentServerUrl: string;
  modo: 'edicion' | 'lectura';
  config: Record<string, unknown>;
};

export type Historial = {
  objectKey: string;
  nombre: string;
  puedeRestaurar: boolean;
  ultimoEditor: string | null;
  ultimaEdicion: string | null;
  vecesEditado: number;
  versiones: Array<{
    id: number;
    numero: number;
    autor: string;
    sizeBytes: string;
    sha256: string | null;
    fecha: string;
  }>;
  eventos: Array<{ tipo: string; actor: string; detalle: string | null; fecha: string }>;
};

/** Ubica un archivo: propio por ruta relativa, o compartido por key completa. */
export type Ubicacion = { path: string } | { key: string };

function query(u: Ubicacion): string {
  return 'path' in u ? `path=${encodeURIComponent(u.path)}` : `key=${encodeURIComponent(u.key)}`;
}

export class ApiError extends Error {}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  // El content-type solo va si hay body: declararlo en un POST vacío hace que
  // el servidor rechace el request por body vacío.
  const headers = init?.body ? { 'Content-Type': 'application/json', ...init.headers } : init?.headers;

  const response = await fetch(path, {
    ...init,
    // Sin la clave cuando no hay headers, en vez de "headers: undefined". El
    // fetch nativo acepta las dos formas, pero Kaspersky reemplaza fetch con
    // un script inyectado que falla con undefined ("Cannot read properties of
    // undefined (reading 'toString')") y rompía, por ejemplo, abrir el editor.
    ...(headers ? { headers } : {}),
    // La sesión viaja en una cookie httpOnly, no en localStorage.
    credentials: 'same-origin',
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { message?: string } | null;
    throw new ApiError(body?.message ?? 'No pudimos completar la operación.');
  }

  return response.json() as Promise<T>;
}

export const api = {
  me: () => request<{ user: User; usedBytes: string }>('/api/auth/me'),

  login: (username: string, password: string) =>
    request<{ user: User }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    }),

  logout: () => request<{ ok: true }>('/api/auth/logout', { method: 'POST' }),

  list: (path: string, cursor?: string) =>
    request<Listing>(
      `/api/files?path=${encodeURIComponent(path)}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`,
    ),

  createFolder: (path: string, name: string) =>
    request<{ path: string }>('/api/files/folder', {
      method: 'POST',
      body: JSON.stringify({ path, name }),
    }),

  remove: (path: string) =>
    request<{ enPapelera: true; objetos: number }>(`/api/files?path=${encodeURIComponent(path)}`, {
      method: 'DELETE',
    }),

  papelera: () => request<{ retencionDias: number; items: ItemPapelera[] }>('/api/trash'),

  restaurarPapelera: (id: number) =>
    request<{ path: string }>(`/api/trash/${id}/restore`, { method: 'POST' }),

  eliminarDePapelera: (id: number) => request<{ ok: true }>(`/api/trash/${id}`, { method: 'DELETE' }),

  vaciarPapelera: () => request<{ eliminados: number }>('/api/trash', { method: 'DELETE' }),

  downloadUrl: (path: string) =>
    request<{ url: string }>(`/api/files/download-url?path=${encodeURIComponent(path)}`),

  uploadUrl: (path: string, file: File) =>
    request<{ url: string; path: string }>('/api/files/upload-url', {
      method: 'POST',
      body: JSON.stringify({
        path,
        filename: file.name,
        sizeBytes: String(file.size),
        contentType: file.type || 'application/octet-stream',
      }),
    }),

  move: (from: string, to: string) =>
    request<{ path: string }>('/api/files/move', {
      method: 'POST',
      body: JSON.stringify({ from, to }),
    }),

  confirmUpload: () =>
    request<{ usedBytes: string }>('/api/files/upload-confirm', { method: 'POST' }),

  sharesReceived: () => request<SharedEntry[]>('/api/shares/received'),
  sharesSent: () => request<SharedEntry[]>('/api/shares/sent'),
  recipients: () => request<Destinatario[]>('/api/shares/recipients'),

  share: (paths: string[], recipientIds: number[], permission: 'read' | 'write') =>
    request<{ created: number; notified: number; fallidos: Array<{ path: string; motivo: string }> }>('/api/shares', {
      method: 'POST',
      body: JSON.stringify({ paths, recipientIds, permission }),
    }),

  unshare: (id: number) =>
    request<{ ok: true }>(`/api/shares/${id}`, { method: 'DELETE' }),

  browseShared: (key: string) =>
    request<SharedEntry[]>(`/api/shares/browse?key=${encodeURIComponent(key)}`),

  sharedDownloadUrl: (key: string) =>
    request<{ url: string }>(`/api/shares/download-url?key=${encodeURIComponent(key)}`),

  publicLinks: () => request<PublicLinkEntry[]>('/api/public-links'),

  createPublicLink: (path: string, expiresAt: string, downloadLimit: number | null) =>
    request<PublicLinkEntry>('/api/public-links', {
      method: 'POST',
      body: JSON.stringify({ path, expiresAt, downloadLimit }),
    }),

  revokePublicLink: (id: number) =>
    request<{ ok: true }>(`/api/public-links/${id}`, { method: 'DELETE' }),

  updateProfile: (datos: { displayName?: string; email?: string | null; theme?: string }) =>
    request<{ user: User }>('/api/auth/me', {
      method: 'PATCH',
      body: JSON.stringify(datos),
    }),

  changePassword: (currentPassword: string, newPassword: string) =>
    request<{ ok: true }>('/api/auth/password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    }),

  officeConfig: (u: Ubicacion) => request<OfficeConfig>(`/api/office/config?${query(u)}`),

  historial: (u: Ubicacion) => request<Historial>(`/api/office/versions?${query(u)}`),

  descargarVersion: (id: number) =>
    request<{ url: string }>(`/api/office/versions/${id}/download`),

  restaurarVersion: (id: number) =>
    request<{ resultado: 'guardado' | 'sin-cambios' }>(`/api/office/versions/${id}/restore`, {
      method: 'POST',
    }),

  adminSummary: () => request<AdminSummary>('/api/admin/summary'),

  adminUsers: () => request<AdminUser[]>('/api/admin/users'),

  adminCreateUser: (datos: {
    username: string;
    displayName: string;
    email: string | null;
    password: string;
    role: Role;
    quotaBytes: string;
  }) => request<AdminUser>('/api/admin/users', { method: 'POST', body: JSON.stringify(datos) }),

  adminUpdateUser: (
    id: number,
    datos: {
      displayName?: string;
      email?: string | null;
      role?: Role;
      quotaBytes?: string;
      password?: string;
      active?: boolean;
    },
  ) => request<{ ok: true }>(`/api/admin/users/${id}`, { method: 'PATCH', body: JSON.stringify(datos) }),

  adminDeleteUser: (id: number, purge: boolean) =>
    request<{ ok: true }>(`/api/admin/users/${id}?purge=${purge}`, { method: 'DELETE' }),

  auditoria: (filtro: FiltroAuditoria, cursor?: string) =>
    request<{ eventos: EventoAuditoria[]; siguiente: string | null }>(
      `/api/admin/audit?${queryAuditoria(filtro, cursor ? { cursor } : {})}`,
    ),

  auditoriaAcciones: () => request<string[]>('/api/admin/audit/acciones'),

  /** Link directo: la descarga la hace el navegador con la cookie de sesión. */
  urlExportarAuditoria: (filtro: FiltroAuditoria) => `/api/admin/audit/export?${queryAuditoria(filtro)}`,
};

/**
 * Sube con XMLHttpRequest en vez de fetch porque es la única forma de tener
 * progreso real de subida en el navegador.
 */
export function uploadToStorage(
  url: string,
  file: File,
  onProgress: (percent: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) onProgress(Math.round((event.loaded / event.total) * 100));
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new ApiError(`El storage rechazó la subida (HTTP ${xhr.status}).`));
    });

    xhr.addEventListener('error', () => reject(new ApiError('Se cortó la conexión al subir.')));
    xhr.send(file);
  });
}

export function formatBytes(value: string | bigint): string {
  let n = Number(value);
  if (!Number.isFinite(n)) return '—';

  const unidades = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  while (n >= 1024 && i < unidades.length - 1) {
    n /= 1024;
    i++;
  }

  return `${n.toFixed(n >= 10 || i === 0 ? 0 : 1)} ${unidades[i]}`;
}

export function formatDate(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// Mismo criterio que el backend: estos formatos abren en el editor. doc, xls,
// ppt, rtf y pdf abren en modo lectura aunque se tenga permiso de edición.
const OFFICE = new Set([
  'docx', 'odt', 'txt', 'rtf', 'doc',
  'xlsx', 'ods', 'csv', 'xls',
  'pptx', 'odp', 'ppt',
  'pdf',
]);

export function abreEnEditor(nombre: string): boolean {
  const punto = nombre.lastIndexOf('.');
  return punto > 0 && OFFICE.has(nombre.slice(punto + 1).toLowerCase());
}

export function urlEditor(u: Ubicacion): string {
  return `/editar?${query(u)}`;
}
